import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PERMISSION_RESOURCE_KEYS,
  PERMISSION_RESOURCE_LABELS_CN,
  emptyRolePermissions,
} from "@glow-price/shared";
import type {
  PermissionResource,
  RolePermissions,
} from "@glow-price/shared";

import { rolesApi, type RoleWithUsage, type RoleWriteInput } from "../api/resources";
import { EnhancedTable, PageHeader } from "../components";

type FormValues = {
  slug?: string;
  nameCn: string;
  description?: string;
};

/** Summarise a permission matrix into a one-line "N 类可读 · M 类可写" string. */
function summarizePermissions(perms: RolePermissions): string {
  let read = 0;
  let write = 0;
  for (const key of PERMISSION_RESOURCE_KEYS) {
    if (perms[key]?.read) read += 1;
    if (perms[key]?.write) write += 1;
  }
  if (read === 0 && write === 0) return "无任何权限";
  return `${read} 类资源可读 · ${write} 类可写`;
}

export function RolesPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RoleWithUsage | null>(null);
  // Local copy of the matrix the modal edits.
  const [matrix, setMatrix] = useState<RolePermissions>(emptyRolePermissions());

  const list = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });

  const isReadOnly = !!editing?.isSystem;

  const saveMutation = useMutation({
    mutationFn: (body: RoleWriteInput) => {
      if (editing) return rolesApi.update(editing.slug, body);
      return rolesApi.create(body);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["roles"] });
      setOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => rolesApi.remove(slug),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const openCreate = () => {
    setEditing(null);
    setMatrix(emptyRolePermissions());
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row: RoleWithUsage) => {
    setEditing(row);
    setMatrix(row.permissions);
    form.setFieldsValue({
      slug: row.slug,
      nameCn: row.nameCn,
      description: row.description ?? undefined,
    });
    setOpen(true);
  };

  const handleSubmit = (values: FormValues) => {
    const body: RoleWriteInput = {
      nameCn: values.nameCn,
      description: values.description?.trim() ? values.description.trim() : null,
      permissions: matrix,
    };
    if (!editing) body.slug = values.slug;
    saveMutation.mutate(body);
  };

  const items = list.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="角色权限"
        description="可在内置角色之外创建自定义权限分组，按资源精细控制读 / 写 / 删。"
        stats={[{ label: `共 ${items.length} 个角色` }]}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建角色
          </Button>
        }
      />

      <EnhancedTable<RoleWithUsage>
        rowKey="slug"
        loading={list.isLoading}
        dataSource={items}
        persistKey="roles"
        exportFilename="roles"
        pagination={false}
        columns={[
          { title: "中文名", dataIndex: "nameCn", width: 140 },
          {
            title: "标识",
            dataIndex: "slug",
            width: 150,
            render: (v: string) => (
              <Typography.Text code style={{ fontSize: 12 }}>
                {v}
              </Typography.Text>
            ),
          },
          {
            title: "说明",
            dataIndex: "description",
            render: (v: string | null) => v ?? "—",
          },
          {
            title: "类型",
            dataIndex: "isSystem",
            width: 90,
            render: (v: boolean) =>
              v ? <Tag color="blue">内置</Tag> : <Tag>自定义</Tag>,
          },
          {
            title: "使用账号数",
            dataIndex: "userCount",
            width: 100,
            align: "right" as const,
          },
          {
            title: "权限概览",
            key: "perms",
            render: (_: unknown, row: RoleWithUsage) =>
              row.slug === "superadmin" ? (
                <Typography.Text type="secondary">始终拥有全部权限</Typography.Text>
              ) : (
                <Typography.Text type="secondary">
                  {summarizePermissions(row.permissions)}
                </Typography.Text>
              ),
          },
          {
            title: "操作",
            key: "actions",
            width: 150,
            render: (_: unknown, row: RoleWithUsage) => {
              const deleteDisabledReason = row.isSystem
                ? "内置角色不可删除"
                : row.userCount > 0
                  ? "仍有账号使用该角色，无法删除"
                  : null;
              return (
                <Space size="small">
                  <Button type="link" size="small" onClick={() => openEdit(row)}>
                    {row.isSystem ? "查看" : "编辑"}
                  </Button>
                  {deleteDisabledReason ? (
                    <Tooltip title={deleteDisabledReason}>
                      <Button type="link" size="small" danger disabled>
                        删除
                      </Button>
                    </Tooltip>
                  ) : (
                    <Popconfirm
                      title="确认删除该角色？"
                      okType="danger"
                      onConfirm={() => deleteMutation.mutate(row.slug)}
                    >
                      <Button type="link" size="small" danger>
                        删除
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ]}
      />

      <Modal
        title={
          editing
            ? `${isReadOnly ? "查看" : "编辑"}角色：${editing.nameCn}`
            : "新建角色"
        }
        open={open}
        width={680}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={isReadOnly ? () => setOpen(false) : () => form.submit()}
        okText={isReadOnly ? "关闭" : "保存"}
        cancelButtonProps={isReadOnly ? { style: { display: "none" } } : undefined}
        confirmLoading={saveMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          disabled={isReadOnly}
          onFinish={handleSubmit}
        >
          {editing ? (
            <Form.Item label="标识">
              <Typography.Text code>{editing.slug}</Typography.Text>
            </Form.Item>
          ) : (
            <Form.Item
              name="slug"
              label="标识（slug）"
              rules={[
                { required: true, message: "请输入标识" },
                {
                  pattern: /^[a-z][a-z0-9_-]*$/,
                  message: "需小写字母开头，仅含小写字母、数字、下划线、连字符",
                },
              ]}
            >
              <Input placeholder="例如 ops_reviewer" autoComplete="off" />
            </Form.Item>
          )}
          <Form.Item
            name="nameCn"
            label="中文名"
            rules={[{ required: true, message: "请输入中文名" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} autoComplete="off" />
          </Form.Item>
        </Form>

        {editing?.slug === "superadmin" ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            内置超级管理员始终拥有全部权限，无需在此配置。
          </Typography.Paragraph>
        ) : null}

        <PermissionMatrix
          value={matrix}
          onChange={setMatrix}
          readOnly={isReadOnly}
        />
      </Modal>
    </div>
  );
}

// ─── permission matrix sub-component ─────────────────────────────────────────

type Op = "read" | "write" | "delete";
const OPS: Op[] = ["read", "write", "delete"];
const OP_LABELS: Record<Op, string> = { read: "读", write: "写", delete: "删" };

function PermissionMatrix({
  value,
  onChange,
  readOnly,
}: {
  value: RolePermissions;
  onChange: (next: RolePermissions) => void;
  readOnly: boolean;
}) {
  // Keep the rendered matrix in sync if the parent swaps it (open another role).
  const [local, setLocal] = useState<RolePermissions>(value);
  useEffect(() => setLocal(value), [value]);

  const emit = (next: RolePermissions) => {
    setLocal(next);
    onChange(next);
  };

  const toggle = (key: PermissionResource, op: Op, checked: boolean) => {
    emit({ ...local, [key]: { ...local[key], [op]: checked } });
  };

  const toggleRow = (key: PermissionResource, checked: boolean) => {
    emit({ ...local, [key]: { read: checked, write: checked, delete: checked } });
  };

  const setAll = (perm: { read: boolean; write: boolean; delete: boolean }) => {
    const next = {} as RolePermissions;
    for (const key of PERMISSION_RESOURCE_KEYS) next[key] = { ...perm };
    emit(next);
  };

  const dataSource = useMemo(
    () => PERMISSION_RESOURCE_KEYS.map((key) => ({ key })),
    [],
  );

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Typography.Text strong>权限矩阵</Typography.Text>
        {!readOnly ? (
          <>
            <Button
              size="small"
              onClick={() => setAll({ read: true, write: false, delete: false })}
            >
              全部只读
            </Button>
            <Button
              size="small"
              onClick={() => setAll({ read: true, write: true, delete: true })}
            >
              全部读写删
            </Button>
            <Button
              size="small"
              onClick={() => setAll({ read: false, write: false, delete: false })}
            >
              全部清空
            </Button>
          </>
        ) : null}
      </Space>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={dataSource}
        columns={[
          {
            title: "资源",
            dataIndex: "key",
            render: (key: PermissionResource) => PERMISSION_RESOURCE_LABELS_CN[key],
          },
          ...OPS.map((op) => ({
            title: OP_LABELS[op],
            key: op,
            width: 70,
            align: "center" as const,
            render: (_: unknown, row: { key: PermissionResource }) => (
              <Checkbox
                checked={local[row.key]?.[op] ?? false}
                disabled={readOnly}
                onChange={(e) => toggle(row.key, op, e.target.checked)}
              />
            ),
          })),
          {
            title: "整行",
            key: "row",
            width: 70,
            align: "center" as const,
            render: (_: unknown, row: { key: PermissionResource }) => {
              const r = local[row.key];
              const allOn = !!r?.read && !!r?.write && !!r?.delete;
              return (
                <Checkbox
                  checked={allOn}
                  disabled={readOnly}
                  onChange={(e) => toggleRow(row.key, e.target.checked)}
                />
              );
            },
          },
        ]}
      />
    </div>
  );
}
